'use client';

import { useState, useEffect } from 'react';
import PricingCard from './PricingCard';
import ComparisonTable from './ComparisonTable';
import PricingCalculator from './PricingCalculator';
import TestimonialCard from './TestimonialCard';
import FAQItem from './FAQItem';
import Icon from '@/components/ui/AppIcon';

interface Feature {
  name: string;
  included: boolean;
  tooltip?: string;
}

interface PricingPlan {
  title: string;
  price: number;
  annualPrice: number;
  period: string;
  description: string;
  features: Feature[];
  isPopular: boolean;
  ctaText: string;
  subscriberCount?: string;
}

interface Testimonial {
  name: string;
  role: string;
  image: string;
  alt: string;
  rating: number;
  text: string;
  plan: string;
}

interface FAQ {
  question: string;
  answer: string;
}

interface ComparisonFeature {
  category: string;
  features: {
    name: string;
    free: boolean | string;
    pro: boolean | string;
    premium: boolean | string;
  }[];
}

export default function PricingInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const pricingPlans: PricingPlan[] = [
  {
    title: 'Free',
    price: 0,
    annualPrice: 0,
    period: 'month',
    description: 'Perfect for beginners exploring AI trading',
    features: [
    { name: 'Preview 2 live signals', included: true },
    { name: 'Basic market analysis', included: true },
    { name: 'Paper trading mode', included: true, tooltip: 'Practice trading without real money' },
    { name: 'Community access', included: true },
    { name: 'Email support', included: true },
    { name: 'Real-time signals', included: false },
    { name: 'Advanced AI insights', included: false },
    { name: 'Priority support', included: false },
    { name: 'API access', included: false }],

    isPopular: false,
    ctaText: 'View Live Signals',
    subscriberCount: '15,000+ users'
  },
  {
    title: 'Pro',
    price: 9,
    annualPrice: 90,
    period: 'month',
    description: 'For active traders seeking consistent profits',
    features: [
    { name: 'Unlimited signals', included: true },
    { name: 'Real-time signal delivery', included: true, tooltip: 'Instant notifications for all trading opportunities' },
    { name: 'Advanced AI analysis', included: true },
    { name: 'Multi-asset coverage', included: true },
    { name: 'Performance analytics', included: true },
    { name: 'Priority email support', included: true },
    { name: 'Trading journal', included: true },
    { name: 'API access', included: false },
    { name: 'White-label options', included: false }],

    isPopular: true,
    ctaText: 'Start Pro Trial',
    subscriberCount: '28,000+ subscribers'
  },
  {
    title: 'Premium',
    price: 19,
    annualPrice: 190,
    period: 'month',
    description: 'Professional-grade tools for serious traders',
    features: [
    { name: 'Everything in Pro', included: true },
    { name: 'Custom AI model training', included: true, tooltip: 'Train AI on your preferred trading strategies' },
    { name: 'API access for automation', included: true },
    { name: 'Dedicated account manager', included: true },
    { name: 'Advanced risk management', included: true },
    { name: 'Portfolio optimization', included: true },
    { name: 'White-label solutions', included: true },
    { name: '24/7 priority support', included: true },
    { name: 'Custom integrations', included: true }],

    isPopular: false,
    ctaText: 'Contact Sales',
    subscriberCount: '7,000+ professionals'
  }];


  const comparisonData: ComparisonFeature[] = [
  {
    category: 'Signal Features',
    features: [
    { name: 'Monthly signal limit', free: '2 signals preview', pro: 'Unlimited', premium: 'Unlimited' },
    { name: 'Real-time delivery', free: false, pro: true, premium: true },
    { name: 'Signal confidence score', free: true, pro: true, premium: true },
    { name: 'Entry/exit recommendations', free: true, pro: true, premium: true },
    { name: 'Risk/reward analysis', free: false, pro: true, premium: true }]

  },
  {
    category: 'AI & Analytics',
    features: [
    { name: 'Basic AI analysis', free: true, pro: true, premium: true },
    { name: 'Advanced pattern recognition', free: false, pro: true, premium: true },
    { name: 'Custom AI model training', free: false, pro: false, premium: true },
    { name: 'Performance analytics', free: false, pro: true, premium: true },
    { name: 'Portfolio optimization', free: false, pro: false, premium: true }]

  },
  {
    category: 'Market Coverage',
    features: [
    { name: 'Crypto markets', free: true, pro: true, premium: true },
    { name: 'Forex pairs', free: false, pro: true, premium: true },
    { name: 'Stocks & indices', free: false, pro: true, premium: true },
    { name: 'Commodities', free: false, pro: false, premium: true }]

  },
  {
    category: 'Tools & Features',
    features: [
    { name: 'Paper trading', free: true, pro: true, premium: true },
    { name: 'Trading journal', free: false, pro: true, premium: true },
    { name: 'Risk management tools', free: false, pro: true, premium: true },
    { name: 'API access', free: false, pro: false, premium: true },
    { name: 'White-label options', free: false, pro: false, premium: true }]

  },
  {
    category: 'Support',
    features: [
    { name: 'Email support', free: true, pro: true, premium: true },
    { name: 'Priority support', free: false, pro: true, premium: true },
    { name: 'Dedicated account manager', free: false, pro: false, premium: true },
    { name: '24/7 support', free: false, pro: false, premium: true }]

  }];


  const testimonials: Testimonial[] = [
  {
    name: 'Sarah Chen',
    role: 'Day Trader',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_173b85801-1763296473353.png",
    alt: 'Professional Asian woman with long dark hair in business attire smiling at camera',
    rating: 5,
    text: 'Upgraded to Pro after my first week. The real-time signals have completely transformed my trading results. Made back my subscription cost in the first day.',
    plan: 'Pro'
  },
  {
    name: 'Marcus Johnson',
    role: 'Investment Advisor',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7469234-1763294888180.png",
    alt: 'Professional Black man with short hair in navy suit smiling confidently',
    rating: 5,
    text: 'Premium plan is worth every penny. The API access and custom AI training have given my clients a significant edge. ROI is exceptional.',
    plan: 'Premium'
  },
  {
    name: 'Elena Rodriguez',
    role: 'Crypto Enthusiast',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13e590e6b-1763296398085.png",
    alt: 'Young Hispanic woman with wavy brown hair in casual attire smiling warmly',
    rating: 5,
    text: 'Started with the free plan to test the waters. The accuracy was so impressive that I upgraded to Pro within a month. Best trading decision I\'ve made.',
    plan: 'Pro'
  }];


  const faqs: FAQ[] = [
  {
    question: 'Can I switch plans at any time?',
    answer: 'Yes, you can upgrade or downgrade your plan at any time. When upgrading, you\'ll be charged the prorated difference immediately. When downgrading, the change takes effect at the end of your current billing cycle, and you\'ll receive a prorated credit.'
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, Mastercard, American Express), debit cards, and PayPal. All payments are processed securely through Stripe with bank-level encryption.'
  },
  {
    question: 'Is there a free trial for paid plans?',
    answer: 'Yes! Pro plan includes a 7-day free trial with full access to all features. Premium plan offers a 14-day trial. No credit card required for the free trial, and you can cancel anytime before the trial ends.'
  },
  {
    question: 'What is your refund policy?',
    answer: 'We offer a 30-day money-back guarantee on all paid plans. If you\'re not satisfied with Signaly for any reason, contact our support team within 30 days of purchase for a full refund, no questions asked.'
  },
  {
    question: 'How accurate are the AI signals?',
    answer: 'Our AI maintains an average confidence score of 87% with a 67% win rate across all markets. Performance varies by market conditions and asset class. All historical performance data is transparently available in your dashboard.'
  },
  {
    question: 'Do you offer discounts for annual billing?',
    answer: 'Yes! Annual billing saves you 2 months compared to monthly billing. Pro annual is $90 (vs $108 monthly) and Premium annual is $190 (vs $228 monthly). That\'s 17% savings on both plans.'
  },
  {
    question: 'Can I use Signaly for multiple accounts?',
    answer: 'Free and Pro plans are for individual use only. Premium plan includes multi-account management and can be used across up to 5 trading accounts. For institutional or team use, contact our sales team for custom enterprise solutions.'
  },
  {
    question: 'What happens if I exceed the signal limit on the free plan?',
    answer: 'You\'ll receive a notification when you\'re approaching your 50-signal monthly limit. Once reached, you can either wait until the next month or upgrade to Pro for unlimited signals. Your account remains active, but new signals won\'t be delivered until reset or upgrade.'
  }];


  const handleCtaClick = (planTitle: string) => {
    if (!isHydrated) return;

    // Free plan redirects to limited live signals dashboard with query parameter
    if (planTitle === 'Free') {
      window.location.href = '/live-signals-dashboard?plan=free';
    } 
    // Redirect Pro and Premium plans to Stripe checkout on subscriptions page
    else if (planTitle === 'Pro' || planTitle === 'Premium') {
      window.location.href = '/subscriptions';
    }
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="animate-pulse space-y-8 px-4 py-16">
          <div className="h-12 bg-muted rounded w-1/2 mx-auto"></div>
          <div className="h-6 bg-muted rounded w-3/4 mx-auto"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {[1, 2, 3].map((i) =>
            <div key={i} className="h-96 bg-muted rounded-2xl"></div>
            )}
          </div>
        </div>
      </div>);

  }

  const savings = billingPeriod === 'annual' ? {
    pro: 9 * 12 - 90,
    premium: 19 * 12 - 190
  } : { pro: 0, premium: 0 };

  return (
    <div className="min-h-screen bg-background">
      <section className="px-4 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-heading font-bold bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Choose the perfect plan for your trading journey. All plans include our AI-powered signals with 87% confidence score.
            </p>

            <div className="inline-flex items-center bg-muted rounded-full p-1">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2 rounded-full font-heading font-semibold transition-all duration-200 ${
                billingPeriod === 'monthly' ? 'bg-primary text-primary-foreground shadow-cyan-glow' : 'text-foreground hover:text-primary'}`
                }>

                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-6 py-2 rounded-full font-heading font-semibold transition-all duration-200 ${
                billingPeriod === 'annual' ? 'bg-primary text-primary-foreground shadow-cyan-glow' : 'text-foreground hover:text-primary'}`
                }>

                Annual
                <span className="ml-2 text-xs bg-success text-success-foreground px-2 py-1 rounded">
                  Save 17%
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {pricingPlans.map((plan) =>
            <PricingCard
              key={plan.title}
              title={plan.title}
              price={billingPeriod === 'annual' ? plan.annualPrice : plan.price}
              period={billingPeriod === 'annual' ? 'year' : plan.period}
              description={plan.description}
              features={plan.features}
              isPopular={plan.isPopular}
              ctaText={plan.ctaText}
              onCtaClick={() => handleCtaClick(plan.title)}
              subscriberCount={plan.subscriberCount} />

            )}
          </div>

          {billingPeriod === 'annual' &&
          <div className="bg-success/10 border border-success rounded-xl p-6 mb-16 text-center">
              <Icon name="SparklesIcon" variant="solid" size={32} className="text-success mx-auto mb-3" />
              <h3 className="text-xl font-heading font-bold text-foreground mb-2">
                Annual Billing Savings
              </h3>
              <p className="text-muted-foreground mb-4">
                Save up to ${savings.premium} per year with annual billing
              </p>
              <div className="flex items-center justify-center space-x-8">
                <div>
                  <p className="text-sm text-muted-foreground">Pro Plan Savings</p>
                  <p className="text-2xl font-heading font-bold text-success">${savings.pro}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Premium Plan Savings</p>
                  <p className="text-2xl font-heading font-bold text-success">${savings.premium}</p>
                </div>
              </div>
            </div>
          }
        </div>
      </section>

      <section className="px-4 py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Detailed Feature Comparison
            </h2>
            <p className="text-muted-foreground">
              Compare all features across our plans to find the perfect fit
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <ComparisonTable data={comparisonData} />
          </div>
        </div>
      </section>

      <section className="px-4 py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-3xl font-heading font-bold text-foreground mb-6">
                Find Your Perfect Plan
              </h2>
              <p className="text-muted-foreground mb-8">
                Not sure which plan is right for you? Use our calculator to get a personalized recommendation based on your trading activity.
              </p>
              <PricingCalculator />
            </div>

            <div>
              <h2 className="text-3xl font-heading font-bold text-foreground mb-6">
                What Our Users Say
              </h2>
              <div className="space-y-6">
                {testimonials.map((testimonial, index) =>
                <TestimonialCard
                  key={index}
                  name={testimonial.name}
                  role={testimonial.role}
                  image={testimonial.image}
                  alt={testimonial.alt}
                  rating={testimonial.rating}
                  text={testimonial.text}
                  plan={testimonial.plan} />

                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-muted-foreground">
              Everything you need to know about our pricing and plans
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8">
            {faqs.map((faq, index) =>
            <FAQItem key={index} question={faq.question} answer={faq.answer} />
            )}
          </div>
        </div>
      </section>

      <section className="px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary rounded-2xl p-8 lg:p-12 text-center">
            <Icon name="ShieldCheckIcon" variant="solid" size={48} className="text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
              30-Day Money-Back Guarantee
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Try Signaly risk-free. If you're not completely satisfied with your subscription within the first 30 days, we'll refund your payment in full—no questions asked.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" variant="solid" size={20} className="text-success" />
                <span>Secure payments via Stripe</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" variant="solid" size={20} className="text-success" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircleIcon" variant="solid" size={20} className="text-success" />
                <span>No hidden fees</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-16 bg-card">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
            Ready to Start Trading Smarter?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join 50,000+ traders making profit with AI-powered signals
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => handleCtaClick('Free')}
              className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:scale-105 hover:shadow-cyan-glow transition-all duration-200">

              Start Free Trial
            </button>
            <button
              onClick={() => window.location.href = '/contact'}
              className="px-8 py-4 bg-muted text-foreground rounded-lg font-heading font-semibold hover:bg-primary hover:text-primary-foreground transition-all duration-200">

              Contact Sales
            </button>
          </div>
        </div>
      </section>
    </div>);

}